import React, { useState, useEffect } from "react";
import { Typography, Button, useMediaQuery } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Toast from "../../utils/Toast";
import Loader from "../../components/Loader";
import { useAuth } from "../../hooks/useAuth";
import apiList from "../../api/apiList";
import AuthShell from "../../components/Auth/AuthShell";
import AuthInput from "../../components/Auth/AuthInput";
import { Role } from "../../constant/RoleConstants";
import "../../assets/fonts/font.css";

const SignIn: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const isMobile = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { storeUser } = useAuth();

  useEffect(() => {
    document.title = "Sign In - Sprooze";
  }, []);

  const redirectToEmailVerification = (userEmail: string) => {
    navigate("/emailverify", {
      state: { email: userEmail, resendOtp: true },
    });
  };

  const handleUserNavigation = (user: any, token: string) => {
    const userData = {
      name: user?.name,
      email: user?.email,
      profile: user?.profile,
      status: user?.status,
      token: token,
      uId: user?.uId,
      isEmailVerify: user?.isEmailVerify,
    };
    storeUser(userData);
  };

  const handlePostLoginNavigation = (user: any) => {
    if (user?.isEmailVerify === false) {
      redirectToEmailVerification(user?.email || email);
      return;
    }

    if (user?.status === Role.HIRING_MANAGER) {
      navigate("/hiring-manager");
      return;
    }

    if (user?.companyId) {
      navigate("/");
      return;
    }

    navigate("/create-company-profile");
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setEmailError("");
    setPasswordError("");

    if (!email || !password) {
      if (!email) setEmailError("Email is required");
      if (!password) setPasswordError("Password is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Invalid email address");
      return;
    }

    if (password.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(apiList.login, { email, password });
      const { token, user } = response?.data?.data || {};

      if (!token || !user) {
        Toast(response?.data?.statusMessage || "Login failed", "error");
        return;
      }

      handleUserNavigation(user, token);
      Toast("Login successful");
      handlePostLoginNavigation(user);
    } catch (error: any) {
      const statusMessage =
        error?.response?.data?.statusMessage || "Something went wrong";
      const isUnverifiedEmail =
        error?.response?.status === 403 &&
        String(statusMessage).toLowerCase().includes("verify your email");

      if (isUnverifiedEmail) {
        Toast(statusMessage);
        redirectToEmailVerification(email);
        return;
      }

      Toast(statusMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Welcome back"
      title="Sign in to Sprooze"
      subtitle="Enter your credentials to access your recruitment dashboard."
      heroHeading="Hire smarter, faster"
      heroText="Manage jobs, candidates, and campaigns from one powerful platform built for modern recruiters."
      isMobile={isMobile}
      footer={
        <>
          Don&apos;t have an account?
          <Link to="/signup" className="auth-footer-link">
            Sign Up
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <AuthInput
          label="Email Address"
          placeholder="you@company.com"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(value) => {
            setEmail(value);
            setEmailError("");
          }}
          error={emailError}
        />

        <AuthInput
          label="Password"
          placeholder="Enter your password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(value) => {
            setPassword(value);
            setPasswordError("");
          }}
          error={passwordError}
        />

        <Link to="/forgotpassowrd/email" className="auth-forgot-link">
          Forgot Password?
        </Link>

        <Button
          variant="contained"
          type="submit"
          className="auth-submit-btn"
          disabled={loading}
        >
          {loading ? <Loader /> : "Sign In"}
        </Button>
      </form>
    </AuthShell>
  );
};

export default SignIn;
